import { FormikValues } from 'formik';
import { GENDER_CODE_IMS } from './ims';
import { Optin } from '../services/optinService';
import { IValidationProperties } from './validation';
import { AddressServiceLanguage } from '../services/addressService';
import { CountryConfig, RegistrationFlowOrchestration } from './apiConfig';

export type OptInState = { code: string; active: boolean };
export type SaveOptInsRequest = {
  optInCodes: Array<OptInState>;
  emailAddress: string;
  brandCode: string;
};

export enum GENDER_CODE_CIAM {
  MALE = 'Male',
  FEMALE = 'Female',
  OTHER = 'Other',
}

export interface PatchCiamUserRequest {
  firstName?: string;
  lastName?: string;
  nickName?: string;
  birthDate?: Date | string;
  gender?: {
    type: GENDER_CODE_CIAM | null;
    custom?: string | null;
  };
  phoneNumber?: string;
  postalCodeRegionalNews?: string;
}

export interface PatchImsUserRequest {
  namesPerson?: {
    nickName?: string;
    firstName?: string | null;
    lastName?: string | null;
  };
  demographicsPerson?: {
    genderCode?: string | null;
    genderCustom?: string | null;
    dateOfBirth?: string | null;
  };
  contactpointsPhone?: {
    phone?: string | null;
  };
  address?: {
    city?: string | null;
    postalCode?: string | null;
    street?: string | null;
    houseNumber?: string | null;
    countryCode?: string | null;
    houseNumberExtension?: string | null;
  };
}

export interface RegistrationFlowFormValues extends FormikValues {
  firstName?: string;
  lastName?: string;
  birthDateDay?: string;
  birthDateMonth?: string;
  birthDateYear?: string;
  gender?: GENDER_CODE_CIAM;
  genderOther?: string;
  optins: Array<Optin>;
  country?: string;
  address: {
    city?: string;
    postalCode?: string;
    street?: string;
    houseNumber?: string;
    box?: string;
  };
  telephone?: string;
}

export enum IdentityLevel {
  MH2 = 'MH2',
  MH3 = 'MH3',
  MH4 = 'MH4',
  MH5 = 'MH5',
  MH6 = 'MH6',
  MH7 = 'MH7',
  MH8 = 'MH8',
  MH9 = 'MH9',
}

export interface RegistrationFlowProps {
  onSubmit: (values: RegistrationFlowFormValues) => void;
  initialFormValues: RegistrationFlowFormValues;
  countryConfig: CountryConfig;
  onCountryChange: (country: string) => void;
  validationProperties: IValidationProperties;
  addressServiceLanguage: AddressServiceLanguage;
  onSubmitClick: () => void;
  validateOnChange: boolean;
  orchestration: RegistrationFlowOrchestration;
}

export const ImsToCiamGenderCodeMapping = {
  [GENDER_CODE_IMS.OTHER]: GENDER_CODE_CIAM.OTHER,
  [GENDER_CODE_IMS.FEMALE]: GENDER_CODE_CIAM.FEMALE,
  [GENDER_CODE_IMS.MALE]: GENDER_CODE_CIAM.MALE,
  [GENDER_CODE_IMS.UNKNOWN]: null,
};

export const CiamToImsGenderCodeMapping = {
  [GENDER_CODE_CIAM.OTHER]: GENDER_CODE_IMS.OTHER,
  [GENDER_CODE_CIAM.FEMALE]: GENDER_CODE_IMS.FEMALE,
  [GENDER_CODE_CIAM.MALE]: GENDER_CODE_IMS.MALE,
};

export const GENDER_OPTIONS_LABEL = {
  [GENDER_CODE_CIAM.MALE]: 'register.gender.values.male',
  [GENDER_CODE_CIAM.FEMALE]: 'register.gender.values.female',
  [GENDER_CODE_CIAM.OTHER]: 'register.gender.values.other',
};

export function isIdentityLevel(value: string): value is IdentityLevel {
  return Object.values(IdentityLevel).includes(value as IdentityLevel);
}

export enum IdentityScreenAction {
  REGISTER = 'register',
  LOGIN = 'login',
  UPGRADE = 'upgrade',
}

export function isIdentityScreenAction(value: string): value is IdentityScreenAction {
  return Object.values(IdentityScreenAction).includes(value as IdentityScreenAction);
}

export enum IdentityScreenProcess {
  SUBSCRIPTION = 'subscription',
  OTHER = 'other',
  PODCAST = 'podcast',
  DS_FRANCAIS = 'ds_francais',
}

export function isIdentityScreenProcess(value: string): value is IdentityScreenProcess {
  return Object.values(IdentityScreenProcess).includes(value as IdentityScreenProcess);
}
